import { FC } from 'react';
import type { PageComponentData } from '@/widgets/prefab-renderer';
import { prefabsStore } from './prefabs-store';
import { ModalOptions, Store, useModalsStore } from './store';
import { ModalProps } from './types';

type PrefabRuntime = typeof import('@/widgets/prefab-renderer');

const mergeDeep = (target: Record<string, unknown>, source: Record<string, unknown>) => {
    const result: Record<string, unknown> = { ...target };
    for (const [key, value] of Object.entries(source)) {
        if (
            value !== null &&
            value !== undefined &&
            typeof result[key] === 'object' &&
            typeof value === 'object' &&
            !Array.isArray(result[key]) &&
            !Array.isArray(value)
        ) {
            result[key] = mergeDeep(result[key] as Record<string, unknown>, value as Record<string, unknown>);
        } else if (value !== null && value !== undefined) {
            result[key] = value;
        }
    }
    return result;
};

type PrefabVariant = {
    options?: Record<string, unknown>;
    children?: PageComponentData[];
};

type CustomPrefabs = Record<
    string,
    {
        defaultVariant?: string;
        prefabs?: Record<string, PrefabVariant>;
    }
>;

type PrefabStructure = {
    defaultVariant?: string;
    prefabs?: Record<string, PrefabVariant>;
};

const applyPrefabOverride = (
    base: PrefabStructure | null | undefined,
    override: PrefabStructure | undefined,
): PrefabStructure | null => {
    if (!base) return override ?? null;
    if (!override) return base;

    let mergedPrefabs = base.prefabs;

    if (override.prefabs) {
        const mergedVariants: Record<string, PrefabVariant> = {
            ...(base.prefabs ?? {}),
        };

        for (const [variantName, overrideVariant] of Object.entries(override.prefabs)) {
            const baseVariant = base.prefabs?.[variantName];
            mergedVariants[variantName] = {
                options: baseVariant?.options
                    ? mergeDeep({ ...baseVariant.options }, overrideVariant.options || {})
                    : overrideVariant.options,
                children: overrideVariant.children ?? baseVariant?.children ?? [],
            };
        }

        mergedPrefabs = mergedVariants;
    }

    return {
        ...base,
        defaultVariant: override.defaultVariant ?? base.defaultVariant,
        prefabs: mergedPrefabs,
    };
};

const getMergedPrefab = (
    PrefabRegistry: PrefabRuntime['PrefabRegistry'],
    prefabName: string,
    templatePrefabs?: CustomPrefabs,
    customPrefabs?: CustomPrefabs,
): PrefabStructure | null => {
    const defaultPrefab = PrefabRegistry[prefabName as keyof typeof PrefabRegistry] as
        | PrefabStructure
        | undefined;

    const withTemplate = applyPrefabOverride(defaultPrefab, templatePrefabs?.[prefabName]);
    return applyPrefabOverride(withTemplate, customPrefabs?.[prefabName]);
};

const createPrefabModalWrapper = (
    PrefabRenderer: PrefabRuntime['PrefabRenderer'],
    prefabData: PageComponentData[],
    contextOptions?: Record<string, unknown>,
    customPrefabs?: CustomPrefabs,
    templatePrefabs?: CustomPrefabs,
): FC<ModalProps> => {
    function PrefabModalComponent({ ...modalProps }: ModalProps) {
        // Merge modal props into context options so they're available to prefab components
        // Use deep merge: user props can override modal props only if they are defined
        const mergedContextOptions = mergeDeep(
            {
                modalId: modalProps.modalId,
                onClose: modalProps.onClose,
                isClosing: modalProps.isClosing,
                closeOnOverlayClick: modalProps.closeOnOverlayClick,
            },
            contextOptions || {},
        );
        return (
            <PrefabRenderer
                data={prefabData}
                contextOptions={mergedContextOptions}
                templatePrefabs={templatePrefabs}
                customPrefabs={customPrefabs}
            />
        );
    }
    PrefabModalComponent.displayName = 'PrefabModalComponent';
    return PrefabModalComponent;
};

export class ModalService {
    public static getState: typeof useModalsStore.getState = () => {
        return useModalsStore.getState();
    };

    public static setState = (...args: Parameters<typeof useModalsStore.setState>) => {
        useModalsStore.setState(...args);
    };

    public static show: Store['show'] = async (...args) => {
        return await useModalsStore.getState().show(...args);
    };

    public static hide: Store['hide'] = async (...args) => {
        return await useModalsStore.getState().hide(...args);
    };

    public static hideAll: Store['hideAll'] = async (...args) => {
        return await useModalsStore.getState().hideAll(...args);
    };

    /**
     * Shows a modal from a prefab JSON file
     * @param prefabName Name of the prefab in PrefabRegistry (e.g., 'AddEditAddressModal')
     * @param props Props to pass to the prefab context (equivalent to props in show method)
     * @param options Options for modal behavior (position, closeAnimationDuration, etc.)
     * @returns Promise resolving to the modal ID
     */
    public static async showPrefab(
        prefabName: string,
        props?: Record<string, unknown>,
        options?: ModalOptions,
    ): Promise<number> {
        const separatorIndex = prefabName.indexOf(':');
        const basePrefabName = separatorIndex === -1 ? prefabName : prefabName.slice(0, separatorIndex);
        const variantName = separatorIndex === -1 ? undefined : prefabName.slice(separatorIndex + 1);

        const { PrefabRenderer, PrefabRegistry } = await import('@/widgets/prefab-renderer');
        const customPrefabs = (prefabsStore.getPrefabs() ?? undefined) as CustomPrefabs | undefined;
        const templatePrefabs = (prefabsStore.getTemplatePrefabs() ?? undefined) as CustomPrefabs | undefined;
        const prefab = getMergedPrefab(PrefabRegistry, basePrefabName, templatePrefabs, customPrefabs);

        if (!prefab) {
            throw new Error(`Prefab "${prefabName}" not found in PrefabRegistry`);
        }

        const fallbackVariantKey = prefab.defaultVariant ?? 'default';
        const prefabVariant =
            prefab.prefabs?.[variantName ?? fallbackVariantKey] ??
            prefab.prefabs?.[fallbackVariantKey] ??
            prefab.prefabs?.default;

        if (!prefabVariant) {
            throw new Error(`Prefab "${prefabName}" does not have a matching variant`);
        }

        // Extract the prefab data from the JSON structure
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const prefabData = (prefabVariant.children || []) as any as PageComponentData[];

        // Merge prefab variant options with runtime props (same as PrefabRenderer does for inline prefabs)
        const mergedProps = {
            ...(prefabVariant.options || {}),
            ...(props || {}),
        };

        // Create a wrapper component that renders the prefab
        // The component receives modalId, onClose, isClosing from the modal system
        const PrefabComponent = createPrefabModalWrapper(
            PrefabRenderer,
            prefabData,
            mergedProps,
            customPrefabs,
            templatePrefabs,
        );

        // Show the modal using the existing show method
        // Pass only the props that are not injected by the modal system (prefabData and props are in closure)
        return await ModalService.show(
            PrefabComponent,
            {} as any /* eslint-disable-line @typescript-eslint/no-explicit-any */,
            options,
        );
    }
}
